<template>
  <UseFocusTrap
    :options="{
      immediate: true,
      onDeactivate: () => emit('closeModal'),
      initialFocus: () => initialFocusableItem,
      fallbackFocus: () => initialFocusableItem,
    }"
  >
    <div
      :style="cssVars"
      class="contact-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="staff-contact-modal-title"
    >
      <div class="contact-modal__overlay">
        <div
          ref="contactModalRef"
          class="contact-modal__container"
          tabindex="-1"
        >
          <!-- Header -->
          <header class="contact-modal__header">
            <button
              :aria-label="$t('pages.staff.contactForm.closeLabel')"
              class="close-btn-container"
              @click="emit('closeModal')"
            >
              <ModalCloseIcon />
            </button>
            <div class="title-wrapper">
              <h4
                id="staff-contact-modal-title"
                class="title"
              >
                {{ $t('pages.staff.sendMessageTo', { person: member.full_name }) }}
              </h4>
            </div>
          </header>

          <!-- Body -->
          <div class="contact-modal__body">
            <form
              v-if="reqInitial"
              @submit.prevent=""
            >
              <!-- eslint-disable vue/no-v-for-template-key -->
              <template
                v-for="field of FIELDS"
                :key="field.id"
              >
                <FormGroup
                  :id="field.id"
                  :type="field.type"
                  :errors="errors[field.id]"
                  :label="field.labelKey && $t(`pages.staff.contactForm.${field.labelKey}`)"
                  :placeholder="field.placeholderKey && $t(`pages.staff.contactForm.${field.placeholderKey}`)"
                  :autocomplete="field.autocomplete"
                  :model-value="formState[field.id]"
                  @update:model-value="handleChange(field.id, $event)"
                />
              </template>
              <!-- eslint-enable vue/no-v-for-template-key -->
            </form>
            <div v-if="reqLoading">
              {{ $t('pages.shared.loading') }}
            </div>
            <ModalContentDialog
              v-if="showSuccess || showError"
              :is-success="showSuccess"
              :recipient="member.full_name"
            />
          </div>

          <!-- Footer -->
          <footer class="contact-modal__footer">
            <template v-if="reqInitial">
              <FormButton
                variant="primary"
                :color-overrides="submitEnabled ? buttonColorOverrides : {}"
                :disabled="!submitEnabled"
                @click="handleSubmit"
                @keypress.enter="handleSubmit"
              >
                {{ $t('pages.staff.sendMessage') }}
              </FormButton>
              <FormButton
                variant="secondary"
                :color-overrides="buttonColorOverrides"
                @click="handleClose"
              >
                {{ $t('pages.shared.cancel') }}
              </FormButton>
            </template>
            <FormButton
              v-if="showSuccess"
              variant="primary"
              :color-overrides="buttonColorOverrides"
              @click="handleClose"
            >
              {{ $t('pages.shared.close') }}
            </FormButton>
            <template v-if="showError">
              <FormButton
                variant="primary"
                :color-overrides="buttonColorOverrides"
                @click="resetForm(true)"
                @keypress.enter="resetForm(true)"
              >
                {{ $t('pages.staff.tryAgain') }}
              </FormButton>
              <FormButton @click="handleClose">
                {{ $t('pages.shared.cancel') }}
              </FormButton>
            </template>
          </footer>
        </div>
      </div>
    </div>
  </UseFocusTrap>
</template>

<script setup>
import { computed, onBeforeMount, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { UseFocusTrap } from '@vueuse/integrations/useFocusTrap/component';
import { useStore } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import { useReCaptcha } from 'vue-recaptcha-v3';
import ModalCloseIcon from '../../../global/svg/ModalCloseIcon.vue';
import ModalContentDialog from './components/ModalContentDialog';
import FormGroup from '../../../global/Forms/FormGroup.vue';
import FormButton from '../../../global/Forms/FormButton';
import { FIELDS } from '../../../global/constants/Staff.constants';

defineOptions({ name: 'StaffContactModal' });

const props = defineProps({
  member: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['closeModal']);

const store = useStore();

const formState = reactive({
  fullName: '',
  email: '',
  message: '',
});

const validationRules = computed(() => ({
  email: { required, email },
  fullName: { required },
  message: { required },
}));

const v$ = useVuelidate(validationRules, formState);

const errors = reactive({});
const contactModalRef = ref(null);

const { executeRecaptcha, recaptchaLoaded, instance } = useReCaptcha();

onBeforeMount(() => {
  instance.value.showBadge();
});

onBeforeUnmount(() => {
  instance.value.hideBadge();
});

async function getRecaptchaToken() {
  await recaptchaLoaded();
  return executeRecaptcha('staff_email');
}

const headingColor = computed(() => store.getters.getThemeHeadingColor);
const overlayColor = computed(() => store.getters.getThemeAlertShadowOverlay);
const reqInitial = computed(() => store.getters['staff/getIsReqInitial']);
const reqLoading = computed(() => store.getters['staff/getIsReqLoading']);
const showSuccess = computed(() => store.getters['staff/getIsReqSuccess']);
const showError = computed(() => store.getters['staff/getIsReqError']);

const initialFocusableItem = computed(() => contactModalRef.value?.getElementsByTagName('input')?.[0] ?? null);

const buttonColorOverrides = computed(() => {
  const color = headingColor.value;
  if (!color) {
    return {};
  }
  return {
    '--button-outline': color,
    '--button-primary-bg': color,
    '--button-primary-bg-hover': color,
    '--button-secondary-bg': `${color}33`,
    '--button-secondary-bg-hover': `${color}4d`,
    '--button-secondary-text': color,
  };
});

const cssVars = computed(() => ({
  '--overlay-color': overlayColor.value,
}));

const submitEnabled = computed(
  () => formState.email.length > 0 && formState.fullName.length > 0 && formState.message.length > 0,
);

function onKeydown(e) {
  if (e.key === 'Escape') {
    handleClose();
  }
}

let recaptchaLabelEl = null;

onMounted(() => {
  window.addEventListener('keydown', onKeydown);
  recaptchaLabelEl = document.createElement('label');
  recaptchaLabelEl.textContent = 'Google Recaptcha';
  recaptchaLabelEl.setAttribute('for', 'g-recaptcha-response');
  recaptchaLabelEl.classList.add('sr-only');
  document.body.appendChild(recaptchaLabelEl);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown);
  if (recaptchaLabelEl?.parentNode) {
    recaptchaLabelEl.parentNode.removeChild(recaptchaLabelEl);
  }
});

function handleChange(inputName, newValue) {
  formState[inputName] = newValue;
}

async function handleSubmit() {
  Object.keys(errors).forEach((key) => delete errors[key]);

  v$.value.$touch();
  if (v$.value.$error) {
    fetchErrors();
    return;
  }

  let recaptchaToken = '';
  try {
    recaptchaToken = await getRecaptchaToken();
  } catch (e) {
    console.error(e);
    return;
  }

  store.dispatch('staff/sendContactEmail', {
    formData: {
      fullName: formState.fullName,
      email: formState.email,
      message: formState.message,
      recaptcha_response: recaptchaToken,
      member: props.member,
    },
    writableApi: useRuntimeConfig().public.WRITABLE_BASE_API_URL,
  });
}

function fetchErrors() {
  for (const { id, validators } of FIELDS) {
    if (!v$.value[id].$error) {
      continue;
    }
    errors[id] = validators.reduce(
      (accum, validator) => ({
        ...accum,
        [validator]: v$.value[id][validator],
      }),
      {},
    );
  }

  if (errors.email?.email?.$invalid) {
    contactModalRef.value?.querySelector('#email')?.focus();
  }
}

function handleClose() {
  resetForm();
  emit('closeModal');
}

function resetForm(isRetry) {
  store.commit('staff/initializeReqStatus');

  if (!isRetry) {
    formState.fullName = '';
    formState.email = '';
    formState.message = '';
  }
}
</script>

<style scoped lang="scss">
$primary-dark: #333333;

.contact-modal {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-content: center;
  align-items: center;

  &__overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    background: var(--overlay-color);
    z-index: 99;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  &__container {
    min-width: 600px;
    max-height: calc(100vh - 1rem);
    background: #ffffff;
    z-index: 100;
    padding: 24px 40px;

    display: flex;
    flex-direction: column;

    @media only screen and (max-width: 599px) {
      min-width: unset;
      min-height: unset;
      padding: 1rem;
    }

    & .contact-modal {
      &__header {
        flex: 1;
        margin-bottom: 24px;
        .title-wrapper {
          .title {
            font-style: normal;
            font-weight: bold;
            font-size: 20px;
            line-height: 24px;
            margin-right: 24px;
            letter-spacing: 0.8px;
            color: $primary-dark;
          }
        }
        .close-btn-container {
          justify-content: flex-end;
          margin-bottom: -24px;
          width: min-content;
          float: right;
          outline: none;
          background: none;
          border: none;
          &:focus {
            outline: -webkit-focus-ring-color auto 1px;
          }
        }
      }
      &__body {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        overflow: auto;
      }
      &__footer {
        flex: 1;
        margin-top: 24px;
      }
    }
  }
}
</style>
